import React, { useState, useEffect } from "react";
import { Loader2, FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { getCallScript, type CallScriptResponse, type KnownQuestion } from "utils/callScriptsApi";
import { toast } from "sonner";

interface CallScriptPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callScriptId: string;
  onSelect: (callScriptId: string) => void;
}

const CallScriptPreviewDialog: React.FC<CallScriptPreviewDialogProps> = ({
  open,
  onOpenChange,
  callScriptId,
  onSelect,
}) => {
  const [loading, setLoading] = useState(true);
  const [callScript, setCallScript] = useState<CallScriptResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch call script when dialog opens
  useEffect(() => {
    if (open && callScriptId) {
      setLoading(true);
      setError(null);
      getCallScript(callScriptId)
        .then((data) => {
          setCallScript(data);
        })
        .catch((err: any) => {
          console.error("Error fetching call script:", err);
          setError("Failed to load call script");
          toast.error("Failed to load call script", {
            description: err.response?.data?.detail || "An unexpected error occurred.",
          });
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setCallScript(null);
      setError(null);
    }
  }, [open, callScriptId]);

  // Get questions in order from ordered_question_ids
  const orderedQuestions = React.useMemo(() => {
    if (!callScript?.questions || !callScript?.ordered_question_ids) return [];

    const questionsMap = new Map(callScript.questions.map(q => [q.id, q]));
    return callScript.ordered_question_ids
      .map(id => questionsMap.get(id))
      .filter((q): q is KnownQuestion => q !== undefined);
  }, [callScript]);

  const handleSelect = () => {
    onSelect(callScriptId);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between pr-6">
            <div>
              <DialogTitle>Call Script Preview</DialogTitle>
              <DialogDescription className="mt-1">
                {callScript?.name || "Loading call script..."}
              </DialogDescription>
            </div>
            {callScript && (
              <Badge variant="outline" className="ml-2 shrink-0">
                {callScript.questions.length} question{callScript.questions.length !== 1 ? "s" : ""}
              </Badge>
            )}
          </div>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Loading call script...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="py-8 text-center">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {!loading && !error && callScript && (
          <div className="space-y-4">
            {orderedQuestions.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>This call script has no questions yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {orderedQuestions.map((question, index) => (
                  <div
                    key={question.id}
                    className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#005151] text-white flex items-center justify-center text-xs font-semibold mt-0.5">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {question.question_text}
                      </p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {question.is_custom && (
                          <Badge variant="secondary" className="text-xs">
                            Custom
                          </Badge>
                        )}
                        {question.procedure_code && (
                          <Badge variant="outline" className="text-xs">
                            Code: {question.procedure_code}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {callScript && (
            <Button
              type="button"
              onClick={handleSelect}
              className="bg-[#005151] hover:bg-[#004040] text-white"
            >
              Use This Script
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CallScriptPreviewDialog;
